/**
 * PreferencesContext — single source of truth for all display preferences:
 *   language, theme, RTL direction, font sizes, UI density.
 *
 * Storage strategy:
 *  - Logged-in users: persisted on the backend via PATCH /api/auth/me/display-preferences
 *    with optimistic local fallback.
 *  - Guests: persisted in localStorage under `noor_albayan_display_prefs`.
 *
 * Side effects:
 *  - sets <html lang> and <html dir>
 *  - sets <html data-theme> ("light" | "dark") respecting "system" mode via
 *    matchMedia('(prefers-color-scheme: dark)')
 *  - injects font-size CSS variables on <html>
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import i18n, { DEFAULT_LANGUAGE, RTL_LANGUAGES, SUPPORTED_LANGUAGES } from '../i18n';
import api from '../services/api';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'noor_albayan_display_prefs';

export const FONT_SIZE_LIMITS = {
  quranArabicFontSize: { min: 20, max: 48, default: 28, step: 2 },
  quranTranslationFontSize: { min: 13, max: 26, default: 16, step: 1 },
  hadithFontSize: { min: 14, max: 28, default: 17, step: 1 },
};

export const DEFAULT_PREFERENCES = {
  language: DEFAULT_LANGUAGE,
  theme: 'dark',
  textPreferences: {
    quranArabicFontSize: FONT_SIZE_LIMITS.quranArabicFontSize.default,
    quranTranslationFontSize: FONT_SIZE_LIMITS.quranTranslationFontSize.default,
    hadithFontSize: FONT_SIZE_LIMITS.hadithFontSize.default,
    uiDensity: 'comfortable',
  },
  enableWordAnalysis: true,
  enableTajwidColoring: false,
  enableTajwidExercise: false,
  lectureVoice: 'male',     // 'male' | 'female' | 'user'
};

function clamp(value, { min, max }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function sanitize(prefs) {
  const out = { ...DEFAULT_PREFERENCES, ...(prefs || {}) };
  if (!SUPPORTED_LANGUAGES.includes(out.language)) out.language = DEFAULT_LANGUAGE;
  if (!['light', 'dark', 'system'].includes(out.theme)) out.theme = 'dark';
  out.enableWordAnalysis = out.enableWordAnalysis !== false;
  out.enableTajwidColoring = out.enableTajwidColoring === true;
  out.enableTajwidExercise = out.enableTajwidExercise === true;
  if (!['male', 'female', 'user'].includes(out.lectureVoice)) {
    out.lectureVoice = 'male';
  }
  const tp = { ...DEFAULT_PREFERENCES.textPreferences, ...(prefs?.textPreferences || {}) };
  tp.quranArabicFontSize = clamp(tp.quranArabicFontSize, FONT_SIZE_LIMITS.quranArabicFontSize);
  tp.quranTranslationFontSize = clamp(tp.quranTranslationFontSize, FONT_SIZE_LIMITS.quranTranslationFontSize);
  tp.hadithFontSize = clamp(tp.hadithFontSize, FONT_SIZE_LIMITS.hadithFontSize);
  if (!['comfortable', 'compact'].includes(tp.uiDensity)) tp.uiDensity = 'comfortable';
  out.textPreferences = tp;
  return out;
}

function readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return sanitize(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeLocal(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {}
}

function effectiveTheme(theme) {
  if (theme === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return 'dark';
  }
  return theme;
}

const Ctx = createContext(null);

export function PreferencesProvider({ children }) {
  const { user, refreshUser } = useAuth();
  const [prefs, setPrefs] = useState(() => readLocal() || DEFAULT_PREFERENCES);
  const hydratedRef = useRef(false);

  // --- Apply preferences as side-effects on <html> ---
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('lang', prefs.language);
    html.setAttribute('dir', RTL_LANGUAGES.has(prefs.language) ? 'rtl' : 'ltr');
    if (i18n.language !== prefs.language) {
      i18n.changeLanguage(prefs.language);
    }
  }, [prefs.language]);

  useEffect(() => {
    const html = document.documentElement;
    const apply = () => {
      html.setAttribute('data-theme', effectiveTheme(prefs.theme));
    };
    apply();
    if (prefs.theme === 'system' && typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: light)');
      const handler = () => apply();
      mq.addEventListener?.('change', handler);
      return () => mq.removeEventListener?.('change', handler);
    }
  }, [prefs.theme]);

  useEffect(() => {
    const html = document.documentElement;
    const tp = prefs.textPreferences;
    html.style.setProperty('--qa-fs-quran-arabic', `${tp.quranArabicFontSize}px`);
    html.style.setProperty('--qa-fs-quran-translation', `${tp.quranTranslationFontSize}px`);
    html.style.setProperty('--qa-fs-hadith', `${tp.hadithFontSize}px`);
    html.setAttribute('data-density', tp.uiDensity);
  }, [prefs.textPreferences]);

  // --- Hydrate from backend when user logs in ---
  useEffect(() => {
    if (!user) {
      hydratedRef.current = false;
      return;
    }
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const remote = user?.preferences?.display;
    if (remote) {
      const merged = sanitize({ ...prefs, ...remote });
      setPrefs(merged);
      writeLocal(merged);
    } else {
      // Push current local prefs to backend (first-time sync)
      api.patch('/auth/me/display-preferences', prefs).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // --- Public API ---
  const persist = useCallback(async (next) => {
    const sanitized = sanitize(next);
    setPrefs(sanitized);
    writeLocal(sanitized);
    if (user) {
      try {
        await api.patch('/auth/me/display-preferences', sanitized);
        refreshUser?.();
      } catch {
        // backend unreachable — keep local copy, retry on next change
      }
    }
  }, [user, refreshUser]);

  const setLanguage = useCallback((language) => {
    if (!SUPPORTED_LANGUAGES.includes(language)) return;
    persist({ ...prefs, language });
  }, [prefs, persist]);

  const setTheme = useCallback((theme) => {
    if (!['light', 'dark', 'system'].includes(theme)) return;
    persist({ ...prefs, theme });
  }, [prefs, persist]);

  const setTextPreference = useCallback((key, value) => {
    if (!FONT_SIZE_LIMITS[key] && key !== 'uiDensity') return;
    const tp = { ...prefs.textPreferences, [key]: value };
    persist({ ...prefs, textPreferences: tp });
  }, [prefs, persist]);

  const bumpFontSize = useCallback((key, delta) => {
    const limits = FONT_SIZE_LIMITS[key];
    if (!limits) return;
    const current = prefs.textPreferences[key];
    const next = clamp(current + delta * (limits.step || 1), limits);
    setTextPreference(key, next);
  }, [prefs, setTextPreference]);

  const reset = useCallback(() => {
    persist(DEFAULT_PREFERENCES);
  }, [persist]);

  const setEnableWordAnalysis = useCallback((enabled) => {
    persist({ ...prefs, enableWordAnalysis: !!enabled });
  }, [prefs, persist]);

  const setEnableTajwidColoring = useCallback((enabled) => {
    persist({ ...prefs, enableTajwidColoring: !!enabled });
  }, [prefs, persist]);

  const setEnableTajwidExercise = useCallback((enabled) => {
    // Activating exercise mode auto-enables tajwid coloring (so colors
    // give a hint after the user answered).
    persist({
      ...prefs,
      enableTajwidExercise: !!enabled,
      enableTajwidColoring: enabled ? true : prefs.enableTajwidColoring,
    });
  }, [prefs, persist]);

  const setLectureVoice = useCallback((voice) => {
    if (!['male', 'female', 'user'].includes(voice)) return;
    persist({ ...prefs, lectureVoice: voice });
  }, [prefs, persist]);

  const value = useMemo(() => ({
    preferences: prefs,
    language: prefs.language,
    direction: RTL_LANGUAGES.has(prefs.language) ? 'rtl' : 'ltr',
    effectiveTheme: effectiveTheme(prefs.theme),
    setLanguage,
    setTheme,
    setTextPreference,
    bumpFontSize,
    setEnableWordAnalysis,
    setEnableTajwidColoring,
    setEnableTajwidExercise,
    setLectureVoice,
    reset,
  }), [prefs, setLanguage, setTheme, setTextPreference, bumpFontSize, setEnableWordAnalysis, setEnableTajwidColoring, setEnableTajwidExercise, setLectureVoice, reset]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePreferences() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePreferences must be inside <PreferencesProvider>');
  return ctx;
}
